import { Form, Formik } from "formik";
import { FC, useCallback, useEffect, useState } from "react";
import Modal from 'react-modal';
import UIFieldTypes from "../../constants/field-ui-type";
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { DynamicFieldDescriptor } from "../../models/DynamicFieldDescriptor";
import PresetCustomField from "../../models/PresetCustomField";
import { getPresetFields } from "../../services/customFieldsService";
import { getPresetSettingById } from "../../services/presetService";
import { showLoadItemError } from "../../services/utilities/ServiceError";
import { useDialog } from "../dialog/DialogContext";
import { SelectType } from "../settingFields/SettingSelectField";
import Wizard from "../wizard/wizard";
import { WizardSummaryContext } from "../wizard/wizardSummaryContext";
import { WizardContentForm } from "./wizardContentForm";
import { WizardSummary } from "./wizardSummary";

export const SystemProfileTypeIds = {
    Controller: '6',
    Asset: '8',
    OperatorSecurity: '18',
    CustomMenus: '38',
    Dashboard: '43',
    Reader: '10000',
    idemia: '59'
}

interface PresetSettingsWizardProps {
    presetId?: string;
    systemProfileId: string;
    parentProfileId?: string;
    isOpen: boolean;
    title: string;
    onClose: () => void;
    presetTemplateType: SelectType;
    onSave: (formFields, createNew) => void;
}

export const PresetSettingsWizard: FC<PresetSettingsWizardProps> = ({ presetId = '', systemProfileId = '', parentProfileId = '', isOpen, title, onClose, presetTemplateType = SelectType.presetInputTemplate, onSave }) => {
    /* Used for provider to gather up the displayable values of the form's fields.
        This is done specifically for select fields, where the label is not the stored value, and the selected value is an id.*/
    const [summary, setSummary] = useState(new Array(0));
    const [step, setStep] = useState(0);
    const { translate } = useLocale();
    const dialog = useDialog();
    const initialSidebarSteps = [{
        title: translate.byKey(TranslationKeys.description),
        description: '',
        substeps: []
    }, {
        title: translate.byKey(TranslationKeys.details),
        description: '',
        showToolTip: false,
        toolTipDescription: '',
        substeps: []
    }, {
        title: translate.byKey(TranslationKeys.summary),
        description: '',
        substeps: []
    }];
    const [sidebarSteps, setSidebarSteps] = useState(initialSidebarSteps);
    const normalSteps = sidebarSteps.filter((_, index) => index!= 1)
    const [typeTemplateId, setTypeTemplateId] = useState<string>("");
    const [supportedFields, setSupportedFields] = useState(new Array<any>(0));
    const resetApbType: string = "Reset APB";
    const initialValues: PresetCustomField = {
        id: 0,
        name: '',
        type: '',
        typeId: 0,
        usedBy: '',
        custom: {},
        sdist: "",
        settingIdToCopy: "",
        profileId: ""
    };
    const [currentItem, setCurrentItem] = useState<PresetCustomField>(initialValues);
    const [templateName, setTemplateName] = useState<string>("");

    useEffect(() => {
        setSummary(new Array(0));
    }, [isOpen === true])

    const handleClose = () => {
        setTypeTemplateId('');
        setSidebarSteps(initialSidebarSteps);
        setStep(0);
        onClose();
    }

    const handleSubmit = (formFields) => {
        onSave(formFields, (+presetId <= 0));
        handleClose();
    };

    const findSettingDataColumnKey = (fieldTypeId) => {
        const uiFieldType: any = Object.values(UIFieldTypes).find((uiFieldType) => {
            return fieldTypeId === uiFieldType.value
        });

        return uiFieldType?.settingDataColumnKey ?? ''
    };

    const addEmptyFieldValues = (value: DynamicFieldDescriptor, outerIndex: number, newPreset): true => {
        const newEmptyCustomValueSelection = {
            isBoolean: false,
            value: '',
            id: value.id,
            elementSysname: value.systemName,
            settingDataId: '',
            settingDataColumnKey: findSettingDataColumnKey(value.fieldTypeId)
        };
        if (newPreset.custom) {
            if (newPreset.custom[value.systemName] === undefined) {
                newPreset.custom[value.systemName] = newEmptyCustomValueSelection;
            }
        }
        else {
            newPreset.custom = {
                [value.systemName]: newEmptyCustomValueSelection
            };
        }
        return true;
    };

    useEffect(() => {
        buildWizardData();
    }, [presetId]);

    const buildWizardData = async () => {
        setSidebarSteps(initialSidebarSteps);
        if (!!presetId) {
            getPresetSettingById(presetId, translate)
                .then(async preset => {
                    getPresetFields(+(preset.typeId ?? 0))
                        .then((newSupportedFields) => {
                            let newPreset: PresetCustomField = { ...preset };
                            newSupportedFields.every((value, index) => addEmptyFieldValues(value, index, newPreset));
                            setCurrentItem(newPreset);
                            newSupportedFields.sort((a, b) => a.ordinal - b.ordinal)
                            setSupportedFields(newSupportedFields);
                            let description = newPreset.name + '\n';
                            const newFirstSidebarStep = { ...sidebarSteps[0], description: description };
                            setSidebarSteps([newFirstSidebarStep].concat(...sidebarSteps.slice(1)));
                            setTemplateName(newPreset.type ?? "");
                        }).catch(error => {
                            showLoadItemError(error, dialog ,translate, handleClose);
                            return;
                        })
                }).catch(error => {
                    showLoadItemError(error, dialog, translate, handleClose);
                    return;
                });
            return;
        }
        setCurrentItem(initialValues);
        setTypeTemplateId('');
        setSupportedFields(new Array<any>(0));
        setTemplateName("");
    }

    const handleWizardFirstNext: ((event: any) => void | undefined) | undefined = (event) => {
        if (!!presetId === false) {
            getPresetFields(+typeTemplateId)
                .then((newSupportedFields) => {
                    let newPreset = { ...event.presetItem, custom: {}, typeId: typeTemplateId }
                    newSupportedFields.every((value, index) => addEmptyFieldValues(value, index, newPreset));
                    setCurrentItem(newPreset);
                    setSupportedFields(newSupportedFields);
                });
        }

        const newFirstSidebarStep = { ...sidebarSteps[0], description: event.itemName };
        setSidebarSteps([newFirstSidebarStep].concat(...sidebarSteps.slice(1)));
        setStep(step + 1);

    };

    const handleWizardSecondNext: ((event: any) => void | undefined) | undefined = (event) => {
        const newSecondSidebarStep = { ...sidebarSteps[1], description: event.detailDescription,
            toolTipDescription: event.toolTipDescription, showToolTip: event.showToolTip };
        setSidebarSteps([sidebarSteps[0], newSecondSidebarStep].concat(...sidebarSteps.slice(2)));
        setStep(step + 1);
    }

    const contentPages = [
        <WizardContentForm
            key={"wizard-1"}
            itemId={presetId}
            systemProfileId={systemProfileId}
            onNext={handleWizardFirstNext}
            onBack={handleClose}
            typeTemplateId={typeTemplateId}
            presetTemplateType={presetTemplateType}
            currentItem={currentItem}
            templateName={templateName}
            setTypeTemplateId={setTypeTemplateId}
            setTemplateName={setTemplateName}
            parentProfileId={parentProfileId}>
        </WizardContentForm>,
        <WizardContentForm
            key={"wizard-2"}
            itemId={presetId}
            systemProfileId={systemProfileId}
            onNext={handleWizardSecondNext}
            onBack={() => setStep(step - 1)}
            typeTemplateId={typeTemplateId}
            supportedFields={supportedFields}
            currentItem={currentItem}
            templateName={templateName}>
        </WizardContentForm>,
        <WizardSummary
            key={"wizard-3"}
            onBack={() => setStep(step - 1)}
            supportedFields={supportedFields}
            templateName={templateName}>
        </WizardSummary>
    ];
    const normalContentPages = contentPages.filter((_, index) => index != 1)

    const updateSummary = (accessor: string, newDisplayValue: string) => {
        if (summary) {
            const existingSummaryEntryIndex = summary.findIndex(item => item.value === accessor);
            const newSummaryEntry = { value: accessor, label: newDisplayValue };
            if (existingSummaryEntryIndex >= 0) {
                summary.splice(existingSummaryEntryIndex, 1, newSummaryEntry);
            }
            else {
                summary.push(newSummaryEntry);
            }
        }
    }

    const getContextValue = useCallback(() => (
        {
            summary: [...summary],
            updateSummary
        }),
        [summary, setSummary],
    )

    return <WizardSummaryContext.Provider value={getContextValue()}>
        <Modal
            isOpen={isOpen}
            ariaHideApp={false}
            overlayClassName="flex w-screen position-float-modal fixed top-0 left-0 h-screen bg-opacity-75 bg-white"
            className="bg-white border-none m-auto shadow select-none outline-none w-content">
            <Formik
                initialValues={currentItem}
                onSubmit={handleSubmit}
                enableReinitialize={true}>
                <Form>
                    <Wizard
                        name={title}
                        onClose={handleClose}
                        step={step}
                        sidebarSteps={templateName !== resetApbType ? sidebarSteps : normalSteps}>
                        {templateName !== resetApbType ? contentPages : normalContentPages}
                    </Wizard>
                </Form>
            </Formik>
        </Modal>
    </WizardSummaryContext.Provider>;
};
